<template>
  <div class="packed-circles-viz">
    <div :id="elId"></div>
    <packed-circles-tooltip
      v-model="tooltip.show"
      ref="tooltip"
      :top="tooltip.top"
      :left="tooltip.left"
      :text="tooltip.text"
    />
  </div>
</template>

<script>
/* eslint-disable func-names */

import * as d3 from 'd3';
import PackedCirclesTooltip from './PackedCirclesTooltip.vue';

export default {
  name: 'PackedCirclesViz',
  components: {
    PackedCirclesTooltip,
  },
  props: {
    value: Object,
    showPercentage: {
      type: Boolean,
      default: false,
    },
    elId: String,
    inputRatio: {
      type: Number,
      default: 1,
    },
  },
  computed: {
    data: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit('input', v);
      },
    },
  },
  data() {
    return {
      isDataLoaded: false,
      chart: {
        svg: null,
        g: null,
        x: null,
        y: null,
        gCircle: null,
        node: null,
        view: null,
        circle: null,
        text: null,
        focus: null,
        diameter: null,
        label: null,
        transition: null,
        circleLabelFontSize: 10,
      },
      chartMargin: { top: 16, right: 0, bottom: 0, left: 0 },
      tooltip: {
        text: '',
        show: false,
        top: 0,
        left: 0,
      },
      width: 0, // Get the size from this.getWidthSize()
      height: 0,
      fixedHeight: 1200,
      ratio: 1,
    };
  },
  watch: {
    data: {
      handler(v) {
        if (!this.isDataLoaded && v) {
          this.isDataLoaded = true;
        } else if (this.isDataLoaded && v) {
          this.updateChart();
        }
      },
      immediate: true,
    },
    isDataLoaded: {
      handler(v) {
        if (v) {
          this.init();
        }
      },
    },
    showPercentage: {
      handler() {
        this.togglePercentage();
      },
    },
  },
  methods: {
    init() {
      // Get width size
      this.width = this.getWidthSize();
      // Recalculate height with ratio
      // Readjust ratio if too small
      this.ratio = this.inputRatio;
      if (this.ratio < 0.7) this.ratio = 0.7;
      this.height = this.fixedHeight * this.ratio;
      const { pack, color } = this;

      const root = pack();
      this.chart.focus = root;
      // const { focus } = this.chart;

      const svg = d3
        .select(`#${this.elId}`)
        .append('svg')
        .style('display', 'block')
        .style('margin', () => {
          if (this.$breakpoint.mobile) return '0';
          return '0 -14px';
        })
        .call(this.responsivefy);

      this.chart.svg = svg;

      const transition = svg.transition().duration(750);
      this.chart.transition = transition;

      const g = svg
        .append('g')
        .attr('class', `chart-container-${this.elId}`)
        .attr('x', () => {
          let xAxis = this.chartMargin.left;
          if (this.$breakpoint.mobile && this.ratio < 1) {
            xAxis = this.width / 2;
          }
          return xAxis;
        })
        .attr('y', () => {
          const yAxis = -(this.chartMargin.bottom + this.height / 2);
          return yAxis;
        });
      this.chart.g = g;

      let widthFactor = 0;
      let heightFactor = 0;
      const gCircle = g
        .selectAll('.packed-circle-container')
        .data(root.descendants().slice(1))
        .enter()
        .append('g')
        .style('cursor', 'pointer')
        .attr('data-id', (e, d) => d)
        .attr('transform', (d) => {
          let xAxis = d.x * this.ratio;
          let yAxis = d.y * this.ratio;
          if (this.$breakpoint.mobile && this.ratio < 1) {
            // Save the width and height factors for the children to factor in
            if (d.depth === 1) {
              widthFactor = this.width / 2 - xAxis;
              heightFactor = this.height / 2 - yAxis;
            }
            xAxis += widthFactor;
            yAxis += heightFactor;
          }
          return `translate(${xAxis}, ${yAxis})`;
        })
        .on('mousemove.packed-circle-container', this.handleTooltip)
        .on('mouseenter.packed-circle-container', this.handleTooltip)
        .on('mouseleave.packed-circle-container', this.removeTooltip);
      this.chart.gCircle = gCircle;

      const node = gCircle
        .append('circle')
        .attr('fill', color)
        .attr('data-id', (e, d) => d)
        .attr('class', (d) => {
          if (d.parent && d.children) return 'packed-circle-node';
          if (d.parent && !d.children) return 'packed-circle-node packed-circle-node--leaf';
          return 'packed-circle-node packed-circle-node--root';
        });
      node.transition(transition).attr('r', (d) => {
        const diameter = d.r * this.ratio;
        // Store to parent attr for the first circle
        if (d.depth === 1) {
          const el = d3.select(`.chart-container-${this.elId}`);
          el.attr('x', () => {
            let xAxis = this.chartMargin.left;
            if (this.$breakpoint.mobile && this.ratio < 1) {
              xAxis = this.width / 2 - diameter;
            }
            return xAxis;
          }).attr('y', () => {
            const yAxis = -(this.chartMargin.bottom + this.height / 2);
            // if (this.$breakpoint.mobile && this.ratio < 1) {
            //   let marginBottom = Math.min(diameter, 92);
            //   if (this.ratio < 0.64) {
            //     marginBottom = marginBottom * (1 + this.ratio) + 72;
            //   } else if (this.ratio > 0.64 && this.ratio < 0.74) {
            //     marginBottom *= 1 + this.ratio;
            //   }
            //   yAxis += marginBottom;
            // }
            return yAxis;
          });
        }
        return diameter;
      });
      this.chart.node = node;

      const rootLabel = gCircle
        .append('text')
        .attr('class', 'packed-circle-root-label')
        .attr('fill', 'black')
        .style('fill-opacity', 0)
        .attr('text-anchor', 'middle')
        .attr('transform', (d) => {
          return `translate(0, ${-(d.r * this.ratio + 50)})`;
        })
        .style('display', (d) => {
          if (d.depth === 1) return 'inline';
          return 'none';
        });
      // Yes/No: % have/have not invested
      rootLabel
        .append('tspan')
        .attr('class', 'packed-circle-root-label__title')
        .attr('x', 0)
        .attr('y', 0)
        .text((d) => {
          if (d.depth !== 1) return '';
          let question = 'have invested';
          if (d.data.name === 'No') question = 'have not invested';
          return `${d.data.name}: ${d.data.totalPercentage}% ${question}`;
        });
      // 2nd line text
      rootLabel
        .append('tspan')
        .attr('class', 'packed-circle-root-label__2nd-line')
        .attr('x', 0)
        .attr('y', 0)
        .attr('dy', '1.5em')
        .text((d) => {
          if (d.depth !== 1) return '';
          if (d.data.name === 'Yes') return 'using these digital platforms';
          return 'using digital platforms because';
        });
      rootLabel.transition(transition).style('fill-opacity', 1);
      this.chart.rootLabel = rootLabel;

      const label = gCircle
        .append('text')
        .attr('class', 'packed-circle-label')
        .attr('text-anchor', 'middle')
        .style('fill-opacity', 0)
        .attr('fill', 'white')
        .attr('data-id', (e, d) => d)
        .style('font-size', () => this.chart.circleLabelFontSize)
        .style('display', (d) => {
          // Displayed text only starts from child circles
          if (d.depth < 2) return 'none';
          if (d.r * this.ratio < 1) return 'none';
          return 'inline';
        });
      label
        // .append('tspan')
        .text((d) => {
          if (d.data.name === 'Others') return '+';
          // const text = d.data.name.substring(0, (d.r * this.ratio) / 3);
          // return text;
          return d.data.name;
        })
        .call(this.wrap, 4)
        .style('font-size', function (d) {
          if (d.data.name === 'Others') return '16px';
          return `${d.scale} px`;
        })
        .attr('transform', (d) => {
          // const labelEl = d3.select(`.packed-circle-label[data-id='${i}']`);
          // const { height: labelHeight } = labelEl.node().getBBox();
          let yAxis = 0;
          if (d.lineNumber > 1) yAxis -= this.chart.circleLabelFontSize;
          // if (d.depth === 1) return `translate(0, ${-(d.r * this.ratio - 20)})`;
          // if (d.data.name === 'Others') return `translate(0, ${(d.r * this.ratio) / 2})`;
          // if (d.lineNumber < 3) return 'translate(0, 0)';
          // if (d.lineNumber > 2) return 'translate(0, -4)';
          return `translate(0, ${yAxis})`;
        });
      label.transition(transition).style('fill-opacity', 1);
      this.chart.label = label;

      // Readjust chart center
      this.chart.g.attr('transform', () => {
        const el = d3.select(`.chart-container-${this.elId}`);
        const xAxis = 0;
        const yAxis = +el.attr('y') / 2 + 20;
        return `translate(${xAxis}, ${yAxis})`;
      });

      // Redraw on window changes
      // d3.select(window).on('resize.test', this.redraw);

      // Readjust height responsiveness
      const calculatedContainerHeight = this.chart.g.node().getBBox().height;
      this.height = calculatedContainerHeight + 120;
      svg.call(this.responsivefy);
    },
    pack() {
      const { width, height, data } = this;
      // const sizeScale = d3.scaleSqrt().range([20, 50]);
      // sizeScale.domain(d3.extent(classes, (d) => d.value));

      const hierarchyData = d3
        .hierarchy(data)
        .sum((d) => d.value)
        .sort((a, b) => b.value - a.value);
      const pack = d3
        .pack()
        .size([width, height])
        // .radius((d) => sizeScale(d))
        .padding(3)(hierarchyData);

      return pack;
    },
    color(d) {
      if (d.depth === 1 && d.data.name === 'Yes') return '#6BA0E0';
      if (d.depth === 1 && d.data.name === 'No') return '#FFC499';
      // Others in yes different bg color
      if (d.depth === 2 && d.data.parent === 'Yes' && d.data.name === 'Others') return '#005EB8';
      if (d.depth === 2 && d.data.parent === 'Yes') return '#005EB8';
      if (d.depth === 2 && d.data.parent === 'No') return '#F09252';
      return 'white';
    },
    handleTooltip(e, d) {
      if (d.depth < 2) return;
      const el = d3.select(e.target);
      const circle = el.select('circle');

      el.style('cursor', 'pointer');
      circle.style('stroke', 'white').attr('stroke-width', 2);

      this.renderTooltip(d, e);
    },
    removeTooltip(e) {
      const el = d3.select(e.target);
      const circle = el.select('circle');

      el.style('cursor', 'inherit');

      circle.style('stroke', 'transparent');

      this.tooltip.show = false;
    },
    renderTooltip(d, e) {
      const mouseXPadding = 20;
      const mouseYPadding = 20;

      // Set tooltip correct position, make sure it doesn't go offside
      const posX = e.layerX;
      const posY = e.layerY;
      let tooltipLeft = posX + mouseXPadding;
      let tooltipTop = posY + mouseYPadding;

      this.tooltip.text = this.renderTooltipText(d);

      const tooltipEl = this.$refs.tooltip.$el;
      const tooltipWidth = tooltipEl.getBoundingClientRect().width;
      const tooltipHeight = tooltipEl.getBoundingClientRect().height;

      // Offisde the x1
      if (tooltipLeft + tooltipWidth >= this.width) {
        tooltipLeft = posX - tooltipWidth - mouseXPadding;
      }
      // Offside the y1
      if (tooltipTop + tooltipHeight >= this.height) {
        tooltipTop = posY - tooltipHeight - mouseYPadding;
      }
      // Offside the x0
      if (tooltipLeft < 0) {
        tooltipLeft = posX + mouseXPadding;
      }
      // Offside the y0
      if (tooltipTop < 0) {
        tooltipTop = posY + mouseYPadding;
      }

      this.tooltip.left = tooltipLeft;
      this.tooltip.top = tooltipTop;
      this.tooltip.show = true;
    },
    renderTooltipText(d) {
      if (d.depth < 2) return '';
      let dataText = '';
      const categoryParent = d.data.data.Parent;
      const yesOrNo = d.data.parent;
      if (categoryParent === 'Country') {
        if (yesOrNo === 'Yes') {
          dataText = `${d.data.realValue || d.data.value}% from ${d.data.data.Category_pop_up} use ${
            d.data.data.Type_pop_up
          }`;
        } else {
          dataText = `${d.data.realValue || d.data.value}% from ${d.data.data.Category_pop_up} said ${
            d.data.data.Type_pop_up
          }`;
        }
      } else if (yesOrNo === 'Yes') {
        dataText = `${d.data.realValue || d.data.value}% of ${d.data.data.Category_pop_up} use ${
          d.data.data.Type_pop_up
        }`;
      } else {
        dataText = `${d.data.realValue || d.data.value}% of ${d.data.data.Category_pop_up} said ${
          d.data.data.Type_pop_up
        }`;
      }
      return `<span class="tooltip-text__p"><b>${d.data.data['Yes/No']}</b></span><br/>
        <span class="tooltip-text__p">${dataText}</span><br>`;
    },
    updateChart() {
      this.redraw();
    },
    togglePercentage() {
      d3.selectAll('.packed-circle-label__percentage')
        .transition(d3.transition().duration(350))
        .style('fill-opacity', (d) => {
          if (d.depth === 1) return 1;
          if (this.showPercentage && d.depth > 1 && d.data.name !== 'Others') return 1;
          return 0;
        });
      // this.redraw();
    },
    wrap(txt, width) {
      const { showPercentage } = this;
      txt.each(function (dT) {
        const text = d3.select(this);
        const words = text
          .text()
          .split(/[ \f\n\r\t\v]+/)
          .reverse(); // Don't cut non-breaking space (\xA0), as well as the Unicode characters \u00A0 \u2028 \u2029)
        let wordCount = 0;
        let line = [];
        let lineNumber = 0;
        const lineHeight = 1.2; // ems
        const y = text.attr('y') !== null ? text.attr('y') : 0;
        const dy = text.attr('dy') !== null ? parseFloat(text.attr('dy')) : 0;
        let tspan = text
          .text(null)
          .append('tspan')
          .attr('x', 0)
          .attr('y', y)
          .attr('alignment-baseline', 'middle')
          .attr('dy', `${dy}em`);

        let word = words.pop();
        while (word) {
          line.push(word);
          tspan.text(line.join(' '));
          if (word.includes('-') || tspan.node().getComputedTextLength() - 10 > dT.r) wordCount += width;
          wordCount += 1;
          // Do the next line
          if (wordCount >= width && line.length > 1) {
            line.pop();
            tspan.text(line.join(' '));
            line = [word];
            tspan = text
              .append('tspan')
              .attr('x', 0)
              .attr('y', y)
              // eslint-disable-next-line no-plusplus
              .attr('dy', `${++lineNumber * lineHeight + dy}em`)
              .attr('alignment-baseline', 'middle')
              .text(word);
            wordCount = 0;
          }
          word = words.pop();
        }

        // Apply font size scale function
        const bbox = this.getBBox();
        const cbbox = this.parentNode.getBBox();
        const scale = Math.min(cbbox.width / bbox.width, cbbox.height / bbox.height);
        dT.scale = scale;

        // Conditional percentage
        d3.select(this)
          .append('tspan')
          .attr('x', 0)
          .attr('y', y)
          // eslint-disable-next-line no-plusplus
          .attr('dy', `${++lineNumber * lineHeight + dy}em`)
          .attr('class', 'packed-circle-label__percentage')
          .style('font-weight', 'bold')
          .style('fill-opacity', (d) => {
            if (d.depth === 1) return 1;
            if (showPercentage && d.depth > 1 && d.data.name !== 'Others') return 1;
            return 0;
          })
          .style('font-size', function (d) {
            return `${d.scale} px`;
          })
          .attr('alignment-baseline', 'middle')
          .text((d) => `${d.data.realValue || d.data.value}%`);

        // Store lineNumber
        dT.lineNumber = lineNumber;
      });
    },
    responsivefy(svg) {
      // container will be the DOM element that the svg is appended to
      // we then measure the container and find its aspect ratio
      const { width, height } = this;
      const aspect = width / height;
      // set viewBox attribute to the initial size
      // control scaling with preserveAspectRatio
      // resize svg on inital page load
      svg
        .attr('viewBox', `0 0 ${width} ${height}`)
        .attr('preserveAspectRatio', 'xMidYMin meet')
        .call(this.resize, aspect);
      // add a listener so the chart will be resized
      // when the window resizes
      // multiple listeners for the same event type
      // requires a namespace, i.e., 'click.foo'
      // api docs: https://goo.gl/F3ZCFr
      d3.select(window).on('resize.test', () => {
        this.resize(svg, aspect);
      });
    },
    resize(svg, aspect) {
      // this is the code that resizes the chart
      // it will be called on load
      // and in response to window resizes gets the width of the container
      // and resizes the svg to fill it while maintaining a consistent aspect ratio
      const { width } = this;

      svg.attr('width', width);
      svg.attr('height', width / aspect);
    },
    getWidthSize() {
      return document.getElementById(this.elId).offsetWidth;
    },
    getHeightSize() {
      return document.getElementById(this.elId).offsetHeight;
    },
    redraw() {
      // Redraw svg
      const container = d3.select(`#${this.elId}`);
      container.node().removeChild(container.select('svg').node());
      this.init();
    },
    recenterChartMargin() {
      //
    },
  },
  mounted() {
    // Helps hot reload init
    if (this.isDataLoaded && this.data && !this.chart.svg) this.init();
  },
};
</script>

<style lang="scss">
.packed-circles-viz {
  position: relative;
}
.packed-circle-node {
  text-align: center;
  border-radius: 100%;
  height: 100%;
}
.packed-circle-label {
  font-family: 'Noto Sans Regular', sans-serif;
  font-style: normal;
  font-size: 10px;
  line-height: 13.2px;
  font-weight: 400;
  text-align: center;
  display: inline-block;
  vertical-align: middle;
  color: white;
  @media #{map-get($media-breakpoints, 'mobile')} {
    line-height: 12px;
  }
}
.packed-circle-root-label {
  font-family: 'Noto Sans Bold', sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 19px;
  line-height: 29.7px;
  color: #121212;

  &__2nd-line {
    font-family: 'Noto Sans Regular', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 24.3px;
  }
}
</style>
